import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Pencil,
  Check,
  X,
  Upload,
  Trash,
  User
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLES = [
  { key: 'inquiry', label: 'Inquiry Team', description: 'For inquiry link visitors' },
  { key: 'support', label: 'Support Team', description: 'For normal support chat' },
  { key: 'deposit', label: 'Deposit Team', description: 'For deposit chat' },
  { key: 'withdrawal', label: 'Withdrawal Team', description: 'For withdrawal chat' },
  { key: 'third_party', label: '3rd Party Withdrawal', description: 'For third-party withdrawal chat' },
  { key: 'calling', label: 'Calling Team', description: 'For calling handler flow' }
];

const FONT_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'bold', label: 'Bold' },
  { value: 'italic', label: 'Italic' },
  { value: 'bold-italic', label: 'Bold Italic' }
];

const DisplayNamesSettings = ({ userId }) => {
  const [settings, setSettings] = useState({
    inquiry_name: 'DWS Team',
    support_name: 'DWS Support',
    deposit_name: 'DWS Deposit',
    withdrawal_name: 'DWS Withdrawal',
    inquiry_dp: null,
    support_dp: null,
    deposit_dp: null,
    withdrawal_dp: null,
    inquiry_font: 'normal',
    support_font: 'normal',
    deposit_font: 'normal',
    withdrawal_font: 'normal',
    third_party_name: 'DWS 3rd Party Withdrawal',
    calling_name: 'DWS Calling Team',
    third_party_dp: null,
    calling_dp: null,
    third_party_font: 'normal',
    calling_font: 'normal'
  });
  const [loading, setLoading] = useState(true);
  const [editingRole, setEditingRole] = useState(null);
  const [tempName, setTempName] = useState('');
  const [tempFont, setTempFont] = useState('normal');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/display-names`);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to load display settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (roleKey) => {
    setEditingRole(roleKey);
    setTempName(settings[`${roleKey}_name`] || '');
    setTempFont(settings[`${roleKey}_font`] || 'normal');
  };

  const handleSave = async () => {
    if (!editingRole) return;

    try {
      await axios.put(`${API}/settings/display-names`, {
        user_id: userId,
        [`${editingRole}_name`]: tempName,
        [`${editingRole}_font`]: tempFont
      });

      setSettings(prev => ({
        ...prev,
        [`${editingRole}_name`]: tempName,
        [`${editingRole}_font`]: tempFont
      }));

      toast.success('Display name updated!');
      setEditingRole(null);
    } catch (error) {
      console.error('Failed to update:', error);
      toast.error('Failed to update');
    }
  };

  const handleCancel = () => {
    setEditingRole(null);
    setTempName('');
    setTempFont('normal');
  };

  const handleUploadDP = async (roleKey, file) => {
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/settings/display-dp/upload?role_type=${roleKey}&user_id=${userId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setSettings(prev => ({
        ...prev,
        [`${roleKey}_dp`]: response.data.dp_url
      }));

      toast.success('Profile picture updated!');
    } catch (error) {
      console.error('Failed to upload:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveDP = async (roleKey) => {
    try {
      await axios.put(`${API}/settings/display-names`, {
        user_id: userId,
        [`${roleKey}_dp`]: null
      });

      setSettings(prev => ({
        ...prev,
        [`${roleKey}_dp`]: null
      }));

      toast.success('Profile picture removed!');
    } catch (error) {
      console.error('Failed to remove:', error);
      toast.error('Failed to remove');
    }
  };

  const getFontStyle = (fontValue) => {
    switch (fontValue) {
      case 'bold': return { fontWeight: 'bold' };
      case 'italic': return { fontStyle: 'italic' };
      case 'bold-italic': return { fontWeight: 'bold', fontStyle: 'italic' };
      default: return {};
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <User size={24} className="text-green-600" />
        Display Names (Customer View)
      </h3>
      <p className="text-sm text-gray-500 mb-6">
        Customize how your team appears to customers in chat headers.
      </p>

      <div className="space-y-4">
        {ROLES.map((role) => {
          const nameKey = `${role.key}_name`;
          const dpKey = `${role.key}_dp`;
          const fontKey = `${role.key}_font`;
          const isEditing = editingRole === role.key;

          return (
            <div
              key={role.key}
              className="border rounded-lg p-4 hover:border-green-300 transition-colors"
            >
              <div className="flex items-start gap-4">
                {/* Profile Picture */}
                <div className="flex-shrink-0">
                  <div className="relative w-16 h-16 rounded-full bg-gray-100 overflow-hidden group">
                    {settings[dpKey] ? (
                      <img
                        src={`${API}${settings[dpKey]}`}
                        alt={role.label}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <User size={32} />
                      </div>
                    )}
                    
                    {/* Upload overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <label className="cursor-pointer p-2 bg-white rounded-full hover:bg-gray-100">
                        <Upload size={18} className="text-gray-700" />
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleUploadDP(role.key, e.target.files[0])}
                          className="hidden"
                          disabled={uploading}
                        />
                      </label>
                      {settings[dpKey] && (
                        <button
                          onClick={() => handleRemoveDP(role.key)}
                          className="ml-1 p-2 bg-white rounded-full hover:bg-red-50"
                        >
                          <Trash size={18} className="text-red-500" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Name and Edit */}
                <div className="flex-1">
                  <div className="text-sm text-gray-500 mb-1">{role.label}</div>
                  <p className="text-xs text-gray-400 mb-2">{role.description}</p>
                  
                  {isEditing ? (
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={tempName}
                        onChange={(e) => setTempName(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                        placeholder="Enter display name"
                        autoFocus
                      />
                      
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600">Font:</label>
                        <select
                          value={tempFont}
                          onChange={(e) => setTempFont(e.target.value)}
                          className="px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-green-500 focus:outline-none"
                        >
                          {FONT_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="flex gap-2">
                        <button
                          onClick={handleSave}
                          className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1 text-sm"
                        >
                          <Check size={16} /> Save
                        </button>
                        <button
                          onClick={handleCancel}
                          className="px-3 py-1 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-1 text-sm"
                        >
                          <X size={16} /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span
                        className="text-lg text-gray-800"
                        style={getFontStyle(settings[fontKey])}
                      >
                        {settings[nameKey] || 'Not Set'}
                      </span>
                      <button
                        onClick={() => handleEdit(role.key)}
                        className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      >
                        <Pencil size={18} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-700">
          <strong>Tip:</strong> These names will appear in the chat header when customers open their chat.
          Hover over profile pictures to upload or remove them.
        </p>
      </div>
    </div>
  );
};

export default DisplayNamesSettings;
